import type { AppAttention, AttentionUrgency, BadgeShape } from '@hibro/shared';
import {
  badgeDiscColor,
  badgeDiscCss,
  describeBadge,
  EMPTY_APP_ATTENTION,
} from '@hibro/shared';

/**
 * Browser mechanics for app-level attention (见 23): the tab's favicon and title.
 *
 * A page has no OS-level way to demand attention — no taskbar to flash, no dock
 * to bounce — so a browser tab has exactly three surfaces, and this uses all of
 * them:
 *
 * - **`navigator.setAppBadge`**, when the app is installed. The only route to a
 *   *real* OS badge from a web page (it lands on the Windows taskbar and the
 *   macOS Dock), so it is tried first and unavailability costs nothing.
 * - **The favicon**, composed here rather than swapped between two static
 *   images: drawing the number onto the icon says "3 unread" where a second
 *   static icon can only say "something".
 * - **The title**, prefixed `(3) `. A prefix, not a blink: the count is
 *   persistent state, and a title that never stops changing is a nuisance in the
 *   tab strip and in the window switcher.
 *
 * A **pulse** is the one thing that does blink, and only until the tab is looked
 * at. That split is the whole contract (见 23): the count is the module's to
 * retract, the blinking is the platform's to end.
 */
export interface AttentionPainter {
  /** Paint the persistent state — badge, favicon, title prefix. */
  setState(state: AppAttention): void;
  /** Start (or restart) the blink that asks the user to come back. */
  pulse(urgency: AttentionUrgency): void;
  /** End the blink without waiting for the tab to become visible. */
  clearPulse(): void;
  dispose(): void;
}

/** How long each blink phase lasts. Slow enough to read, fast enough to notice. */
const PULSE_INTERVAL_MS = 900;

/**
 * Leading marker during a pulse. A glyph rather than a word: this runs before
 * any translator exists in the page, and a bare disc needs no translation.
 */
const PULSE_MARKER = '\u25CF ';

/** Favicon canvas edge. Browsers downsample from whatever we hand them. */
const ICON_SIZE = 32;

const DISC_TEXT = '#ffffff';

export interface AttentionPainterOptions {
  /**
   * Report a failure that leaves part of the paint undone. There is no fallback
   * icon: substituting one would hide that the branding icon failed to load
   * while making the badge look fine.
   */
  warn: (message: string, data?: Record<string, unknown>) => void;
}

export function createAttentionPainter({
  warn,
}: AttentionPainterOptions): AttentionPainter {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const baseIconHref = link?.href;
  const baseTitle = document.title;

  let state: AppAttention = EMPTY_APP_ATTENTION;
  let pulsing = false;
  /** Which half of the blink we are in: `true` = blank. Ignored when not pulsing. */
  let dark = false;
  let timer: ReturnType<typeof setInterval> | undefined;
  let baseIcon: Promise<HTMLImageElement | undefined> | undefined;
  /** The blank frame's data URL, encoded once (见 {@link blankIcon}). */
  let blankIconHref: string | undefined;
  /**
   * Guards against an out-of-order paint: composing an icon is asynchronous, so
   * a burst of updates can land its canvases in any order. Only the newest one
   * is allowed to reach the DOM.
   */
  let paintToken = 0;

  function loadBaseIcon(): Promise<HTMLImageElement | undefined> {
    baseIcon ??= new Promise<HTMLImageElement | undefined>((resolve) => {
      if (!baseIconHref) {
        warn('attention: no favicon link to decorate');
        resolve(undefined);
        return;
      }
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', () => {
        warn('attention: the branding icon could not be loaded', {
          href: baseIconHref,
        });
        resolve(undefined);
      });
      image.src = baseIconHref;
    });
    return baseIcon;
  }

  /**
   * The blank half of a blink: a fully transparent icon.
   *
   * Encoded from an untouched canvas rather than written down as a base64
   * literal. A canvas starts transparent, so this is transparent by
   * construction — whereas the literal it replaces turned out to be a
   * half-opaque green pixel, which nothing in review or in the types could have
   * caught, and which showed up in the tab strip as a green square.
   */
  function blankIcon(): string {
    if (blankIconHref === undefined) {
      const canvas = document.createElement('canvas');
      canvas.width = ICON_SIZE;
      canvas.height = ICON_SIZE;
      blankIconHref = canvas.toDataURL('image/png');
    }
    return blankIconHref;
  }

  function renderTitle(shape: BadgeShape): void {
    const prefix = shape.kind === 'count' ? `(${shape.label}) ` : '';
    // Blinks in step with the favicon, and it has to: **the favicon is not the
    // only surface, and in the most important one it is not visible at all.**
    // The Windows taskbar button and its hover preview show the *browser's*
    // icon plus the window title, never the page's favicon — so for a user whose
    // browser is minimized, this marker is the entire pulse. A steady marker
    // there would say "unread", which the `(3)` prefix already says, and nothing
    // would be asking them to come back.
    const marker = pulsing && !dark ? PULSE_MARKER : '';
    document.title = `${marker}${prefix}${baseTitle}`;
  }

  /**
   * The OS-level badge, where the browser offers one. Only an installed app has
   * it, so an absent API is the normal case, not a failure — the favicon below
   * is what everyone else sees.
   */
  function renderAppBadge(shape: BadgeShape): void {
    const badging = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (shape.kind === 'none') {
      void badging.clearAppBadge?.().catch(() => undefined);
      return;
    }
    // `setAppBadge()` with no argument is the platform's own "dot" — a marker
    // with no number — which is exactly what a dot badge means.
    void badging
      .setAppBadge?.(state.count > 0 ? state.count : undefined)
      .catch(() => undefined);
  }

  async function renderFavicon(shape: BadgeShape): Promise<void> {
    if (!link || !baseIconHref) {
      return;
    }
    const token = ++paintToken;
    // The dark half of a blink is the icon *gone* — the whole tab entry goes
    // blank and comes back, which is what catches an eye pointed somewhere
    // else. Tinting it instead (an earlier version laid a translucent red over
    // the artwork) changes every pixel too, but reads as the icon having broken
    // rather than as the app asking for something.
    if (pulsing && dark) {
      link.href = blankIcon();
      return;
    }
    // Nothing to draw and nothing blinking: put the original file back rather
    // than a re-encoded copy of it, so the browser can use its cached decode.
    if (shape.kind === 'none' && !pulsing) {
      link.href = baseIconHref;
      return;
    }
    const image = await loadBaseIcon();
    if (!image || token !== paintToken) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = ICON_SIZE;
    canvas.height = ICON_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      warn('attention: this browser gave no 2d canvas context');
      return;
    }
    ctx.drawImage(image, 0, 0, ICON_SIZE, ICON_SIZE);
    if (shape.kind !== 'none') {
      drawBadge(ctx, shape);
    }
    if (token !== paintToken) {
      return;
    }
    link.href = canvas.toDataURL('image/png');
  }

  function render(): void {
    const shape = describeBadge(state);
    renderTitle(shape);
    renderAppBadge(shape);
    void renderFavicon(shape);
  }

  /** End the blink; the badge stays, because the unread work does. */
  function stopPulse(): void {
    if (!pulsing) {
      return;
    }
    pulsing = false;
    dark = false;
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
    render();
  }

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      stopPulse();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    setState: (next) => {
      state = next;
      render();
    },
    pulse: (urgency) => {
      // A `critical` pulse reached here past the host's visibility gate, which
      // means the user *is* looking — blinking a tab they are reading is noise,
      // and the badge already changed. Nothing further to do.
      if (urgency !== 'critical' && document.visibilityState === 'visible') {
        return;
      }
      if (pulsing) {
        return;
      }
      pulsing = true;
      // Straight into the blank frame, so the pulse is visible immediately
      // rather than a full interval later.
      dark = true;
      render();
      timer = setInterval(() => {
        dark = !dark;
        render();
      }, PULSE_INTERVAL_MS);
    },
    clearPulse: () => {
      stopPulse();
    },
    dispose: () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPulse();
    },
  };
}

/** Draw the unread decoration in the icon's bottom-right corner. */
function drawBadge(ctx: CanvasRenderingContext2D, shape: BadgeShape): void {
  const radius = shape.kind === 'dot' ? ICON_SIZE * 0.22 : ICON_SIZE * 0.34;
  const cx = ICON_SIZE - radius;
  const cy = ICON_SIZE - radius;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = badgeDiscCss(badgeDiscColor(shape));
  ctx.fill();
  if (shape.kind !== 'count') {
    return;
  }
  // Shrink the type for a longer label instead of growing the disc: the icon
  // size is the browser's to decide.
  ctx.fillStyle = DISC_TEXT;
  ctx.font = `bold ${Math.round(radius * (shape.label.length > 2 ? 0.9 : 1.2))}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(shape.label, cx, cy + 1);
}
